import api from './api';
import { OVERVIEW_ZONE_COLORS, OVERVIEW_SHAPE_COLORS, OVERVIEW_DEFAULT_THRESHOLDS } from '../theme/overviewColors';
import type { VpaToggles } from '../utils/vpaIndicators';
import { VPA_TOGGLE_DEFAULTS } from '../utils/vpaIndicators';

export type SessionLineStyle = 'solid' | 'dashed' | 'dotted';

/** Full resolved payload. Stored templates may omit any field — consumers
 * deep-merge stored payload over DEFAULT_CHART_SETTINGS. */
export interface ChartSettingsPayload {
  version: 1;
  candle: {
    risingColor: string;
    fallingColor: string;
    borderMatchesFill: boolean;
    wickMatchesFill: boolean;
    /** When false, candles render body-only (no high/low wicks). Default true. */
    showWick?: boolean;
    /** CLN candle type — linear-regression length for the LR body layer.
     *  Default 14, matching the LR_LEN used by CSL/CLH. */
    clnLength?: number;
    /** CLN candle type — draw a border accent on regression bodies printed on
     *  thin participation (volume contradiction). Default OFF: it is a
     *  diagnostic, and the clean-chart principle is that CLN reads as candles.
     *  It can never change the hue, the fill, or the price geometry. */
    clnVolumeAccent?: boolean;
    /** LR body chip (overlays.lrBody) on a non-CLN candle type — ghost the candles
     *  under it the way CLN does (fade + LR hue, strategy candle colours suppressed).
     *  Default ON. Off = the candles keep their own colours with the body on top.
     *  The CLN candle type always ghosts. */
    lrGhost?: boolean;
    /** CLN HTF band — run the CLN regression on the Strategies-mapped HIGHER
     *  timeframe and draw a ribbon connecting those bodies behind the chart.
     *  Context only; it emits no signal and changes no price geometry.
     *  Deliberately NOT gated on the CLN candle type: the band is defined on
     *  the regression of the raw closes, so it means the same thing on
     *  Regular / HA / CSH / CSL / CLH — the same reasoning that makes the
     *  AV_Band slow-HMA colour display-type independent. Default OFF.
     *
     *  LEGACY since 2026-09-25: the band is now the "HTF Band" indicator chip
     *  (overlays.clnHtfBand) with its own `htfBand` settings group. This key is
     *  read ONLY as the chip's value until the user first clicks the chip, so a
     *  template that had the band on keeps it. The dialog no longer writes it. */
    clnHtfBand?: boolean;
    /** Fill opacity of the CLN HTF band, 0.02–0.40. It sits BEHIND the candles,
     *  so this only has to read as a wash — above ~0.25 it starts competing
     *  with the bodies it is meant to sit under. */
    clnHtfBandOpacity?: number;
    /** Extend the CLN HTF band through the STILL-FORMING higher-timeframe bar,
     *  so the context reaches the last candle instead of stopping up to one HTF
     *  bar short. That segment repaints as the HTF bar develops, so it is drawn
     *  with a lighter fill and a dashed edge. Default ON — the whole point of a
     *  context band is the context you are trading in right now. */
    clnHtfBandForming?: boolean;
  };
  layoutCandleCount: { single: number; split: number; quad: number };
  liveUpdateSec: number;
  /** Show the crosshair info tooltip (OHLC / Trend / Strategy / VPA box that
   *  follows the cursor). Off is handy in split layouts where the box
   *  overlaps the other pane. Crosshair sync between panes is unaffected. */
  crosshairTooltip: boolean;
  cpr: {
    opacity: number;
    showMidline: boolean;
    // Round 33 — directional fill colors. Rising = both TC and BC up vs prev
    // period (or one up & none down), Falling = mirror, Neutral = otherwise.
    risingColor: string;
    fallingColor: string;
    neutralColor: string;
    /** When true, render a projected band past the current period boundary using
     * the developing high/low/close so the user can see the next CPR
     * before it materialises. */
    showProjection: boolean;
  };
  poc: {
    opacity: number;
    // Round 33 — directional fill colors. Same Rising/Falling/Neutral semantics
    // as CPR, applied independently to C-POC and H-POC bands.
    risingColor: string;
    fallingColor: string;
    neutralColor: string;
    showProjection: boolean;
  };
  /** AvgCloud (was CEnv) — dynamic cloud between LR_HAMA_Band Avg edge and
   *  Combined Midline. `opacity` is kept for back-compat with saved templates;
   *  the new directional colors live in `avgCloud`. */
  envelope: { opacity: number };
  /** AvgCloud — directional fill between the LR_HAMA_Band edge and the
   *  Combined Midline (CMid). R72 restored the originally-planned design
   *  now that CMid computes correctly (R70 + R71 fixes). Cloud paints
   *  bullish when LR_HAMA band sits ABOVE CMid, bearish when below.
   *  Colours and opacity are user-configurable; the `reference` field is
   *  retained on the type as optional for back-compat with saved templates
   *  that still carry it — it's no longer read. */
  avgCloud: {
    bullishColor: string;
    bearishColor: string;
    neutralColor: string;
    opacity: number;
    /** @deprecated — kept for back-compat with saved templates only. R72
     *  hard-wired the reference to CMid; this field is no longer read. */
    reference?: 'cpoc' | 'ema200' | 'ema128';
  };
  /** Combined Midline (CMid) — color and stroke width for the dashed mid line. */
  cMid: { color: string; lineWidth: number };
  /** BBR100 — %B of the HLC3 Bollinger band rescaled so the lower band reads
   *  -100, the basis 0 and the upper band +100. Two independent surfaces:
   *    - overboughtColor / oversoldColor tint the CANDLES at reversal bars
   *      (overlay `bbrColor`, the original behaviour);
   *    - the rest style the OSCILLATOR curve (overlay `bbrOsc`).
   *  bbLen / bbMult feed BOTH, so the curve and the tinted bars can never
   *  disagree about where the bands are. */
  bbr: {
    overboughtColor: string;
    oversoldColor: string;
    bbLen: number;
    bbMult: number;
    /** true = own lower pane, false = overlaid on the price pane. */
    pane: boolean;
    lineColor: string;
    lineWidth: number;
    lineMode: 'step' | 'smooth';
    obLevel: number;
    osLevel: number;
    /** Draw the chart-timeframe BBR curve. false = show only the higher
     *  timeframe; guides, zones and the BW / BD / X marks on price are kept. */
    showLtf: boolean;
    /** Also draw BBR of the Strategies-mapped HIGHER timeframe in the same pane
     *  (utils/oscHtf.ts). Visual only — no signal reads it. Rise/fall coloured
     *  like the chart curve; `htfColor` applies when slope colouring is off. */
    showHtf: boolean;
    htfColor: string;
    htfLineWidth: number;
  };
  vspk: { color: string };
  ltp: { lineColor: string; lineWidth: number };
  sessions: {
    enabled: boolean; color: string; opacity: number; style: SessionLineStyle;
    /** Where the session/period vertical line is anchored:
     *  - default    : exchange day-start (e.g. 05:30 Delta / 09:15 NSE) with the
     *                 existing LTF→day/week/month grouping.
     *  - deltaExpiry: Delta India option expiry — daily at deltaExpiryTime IST
     *                 (incl. weekends, since daily crypto options expire every day).
     *  - nseWeekly  : NSE weekly option expiry — nseExpiryDow at nseExpiryTime IST.
     *  - nseMonthly : NSE monthly option expiry — last nseExpiryDow of the month. */
    mode?: 'default' | 'deltaExpiry' | 'nseWeekly' | 'nseMonthly';
    /** 0=Sun … 6=Sat. Default 2 (Tuesday — NSE rule since 1 Sep 2025). */
    nseExpiryDow?: number;
    /** IST 'HH:MM' for NSE weekly/monthly expiry. Default '15:30'. */
    nseExpiryTime?: string;
    /** IST 'HH:MM' for Delta daily option expiry. Default '17:30'. */
    deltaExpiryTime?: string;
  };
  grid: { opacity: number };
  /** Show Delta perpetuals' mark-price as a dotted line (Delta only; no-op elsewhere). */
  markPrice: { enabled: boolean; color: string };
  /** LRBnd — band drawn between CSL open & close. Independent of selected candle type. */
  lrBnd: { color: string; opacity: number };
  /** FBO (False Breakout) signal — only `period` is configurable; other inputs
   *  use the source pinescript's defaults. */
  fbo: { period: number };
  /** Wave (WaveTrend). channelLength / averageLength feed BOTH the ▲▼ arrows
   *  (overlay `wave`) and the oscillator curve (overlay `waveOsc`).
   *
   *  SCALE: wt1 runs about -69..+71 and reaches -100 on 0.00% of bars, so its
   *  ±60 levels are NOT comparable to BBR's ±100 by value — only by rarity
   *  (each tags ~5.8% of bars). TradingChart aligns the two by pinning their
   *  price scales, never by rescaling these numbers, so the crosshair keeps
   *  reporting true values. */
  wave: {
    channelLength: number;
    averageLength: number;
    /** true = own lower pane, false = overlaid on the price pane. */
    pane: boolean;
    /** Draw wt2 (the 4-bar SMA of wt1) alongside wt1. */
    showWt2: boolean;
    lineColor: string;
    wt2Color: string;
    lineWidth: number;
    lineMode: 'step' | 'smooth';
    obLevel: number;
    osLevel: number;
    /** Draw the chart-timeframe Wave curve (and wt2). false = show only the
     *  higher timeframe; guides, zones and the BW / BD / X marks are kept. */
    showLtf: boolean;
    /** Also draw Wave (wt1) of the Strategies-mapped HIGHER timeframe in the
     *  same pane (utils/oscHtf.ts). Visual only — no signal reads it. Rise/fall
     *  coloured like the chart curve; `htfColor` applies when slope colouring is off. */
    showHtf: boolean;
    htfColor: string;
    htfLineWidth: number;
  };
  /** BBR × Wave — the conjunction, and the shared furniture of the oscillator pane.
   *
   *  MEASURED 2026-09-19 over 171 Delta symbols / ~102k daily bars: with both
   *  oscillators at or below their oversold level AND BBR ticking up against the
   *  previous bar, the next 5 days returned +2.50% against a -0.34% baseline
   *  (58.2% win vs 44.0%), firing on 156 of 171 symbols and holding out of sample
   *  (train edge +2.19, test +3.51).
   *
   *  It is deliberately NOT the BBR-crosses-Wave rule it looks like: that measured
   *  +1.21% scale-matched, and a cross anywhere below zero measured -1.17%. BBR
   *  oversold ALONE is worth +0.01%. The conjunction is the whole effect.
   *
   *  `enableShort` defaults OFF: the mirrored rule does not replicate across a
   *  65/35 time split (train edge -0.94, test +5.66 — a sign flip). */
  bbrWave: {
    /* ── Which candles BBR and Wave COMPUTE on.
     *
     *    Deliberately NOT the chart's display candle type. Switching the chart
     *    to Heikin-Ashi to read the trend used to re-compute both oscillators on
     *    synthetic prices, so levels moved and marks appeared or vanished for a
     *    reason that had nothing to do with the market. This setting governs the
     *    BBR curve, the Wave curve, the BW/BD/X marks AND the BBR candle tint,
     *    so all of them always tell the same story.
     *
     *    MEASURED 2026-09-19 (verifyBwSource.ts), indicator series from the
     *    source, prices/ATR/EMA50 always REAL, crypto | equity:
     *
     *      Regular   +3.60% / 60.0% win  |  +3.25% / 70.8%   <- default
     *      HA        +2.80% / 60.5%      |  +3.30% / 71.3%
     *      CLH       +3.05% / 49.1%      |  +4.58% / 62.1%
     *      CLN       +1.38% / 58.0%      |  +2.71% / 66.1%
     *
     *    A smoothed source draws a SMALLER curve but does not fire less often —
     *    every one of them fires MORE. BBR is a position inside a Bollinger
     *    band, so smoothing the source narrows the band too and the oscillator
     *    saturates sooner. 'Regular' is the default because it is what the rule
     *    was measured on and it still returns most on crypto. */
    srcCandle: 'Regular' | 'HA' | 'CLH' | 'CLN';
    /* ── Colour both curves by their OWN SLOPE ──────────────────────────────
     *
     * Rising reads bullish, falling bearish, on BOTH lines. The direction of an
     * oscillator is the thing you are actually watching here - the measured BW
     * entry is "both oversold AND BBR RISING", so the turn is the signal and a
     * single flat hue hides it until the arrow prints.
     *
     * A flat bar carries the previous direction and the leading run is
     * back-filled with the first direction the curve actually takes
     * (stepDirections, utils/clhBand.ts) - so a long hold between two falls
     * does not paint as a third colour, and the line never opens in a colour it
     * has not earned. Per-point colour on a LineSeries is honoured even in
     * stepped mode: v5 strokes each segment with the LEFT point's colour.
     *
     * Off restores the flat bbr.lineColor / wave.lineColor. */
    slopeColor?: boolean;
    riseColor?: string;
    fallColor?: string;
    /** Zero line across the oscillator pane. Not decoration: 0 is the level the
     *  shipped BW exit fires at (bbrWave.exitLevel), so the pane was drawing the
     *  two levels that ARM the rule and not the one that CLOSES it. Dimmed,
     *  because it is a reference and not a reading. */
    showMidline?: boolean;
    midlineColor?: string;
    showSignals: boolean;
    /** Require BBR to be rising vs the previous bar. This is the trigger. */
    requireTurn: boolean;
    buyColor: string;
    shortColor: string;
    guideColor: string;
    /** Shade the oversold / overbought bands so the actionable zone reads at a glance. */
    showZones: boolean;
    /* The two zones get their OWN colours and opacities.
     *
     * They used to borrow settings.bbr.overboughtColor / oversoldColor - the
     * CANDLE TINT colours - which coupled two unrelated things: restyling the
     * tint silently restyled the oscillator bands, and if both tint colours had
     * been set to the same hue the two zones became indistinguishable. Which is
     * exactly what happened.
     *
     * Defaults are reddish above and greenish below, and they are theme-aware:
     * a 10% #c62828 over white reads as a pale pink, but over #0D1117 it is
     * almost invisible, so the dark chart lifts them to the 400-weight hues.
     * A colour the user has actually picked is never lifted. */
    obZoneColor: string;
    osZoneColor: string;
    obZoneOpacity: number;
    osZoneOpacity: number;
    /**
     * Tint the WHOLE oscillator pane by which curve is on top: BBR above Wave
     * greenish, at or below reddish. Off by default - it is a second background
     * on a pane that already shades its overbought and oversold bands, so it is
     * opt-in rather than something a user has to discover and switch off.
     *
     * COMPARED ON THE PINNED SCALES, NOT RAW. BBR runs about -133..+148 against
     * a threshold of 100, wt1 about -69..+71 against 60, and the pane pins each
     * to +/-1.5x its own threshold so the two rows align. A raw `bbr > wt1`
     * would disagree with what is on screen most of the time; dividing each by
     * its own overbought level reproduces the visual "which line is higher".
     *
     * Needs BOTH curves on: a tint derived from a curve that is not drawn is
     * not readable, so it renders only when BBR and Wave are both shown.
     */
    showRelZones: boolean;
    relAboveColor: string;
    relBelowColor: string;
    relZoneOpacity: number;
    /** @deprecated The single shared opacity the two zones used before the
     *  split. Still read as a `??` fallback, but be honest about what that is
     *  worth: resolvePayload walks the DEFAULT keys and fills any the saved
     *  payload lacks, so obZoneOpacity/osZoneOpacity are ALWAYS present and the
     *  fallback never fires on a real reload. An opacity customised before the
     *  split is therefore NOT carried over - it has to be set again, now per
     *  zone. Kept so nothing that still reads the old key breaks. */
    zoneOpacity: number;

    /* ── Exits. Every entry mark gets a paired X, so a trade on the chart has
     *    a beginning and an end rather than only a beginning.
     *
     *    MEASURED 2026-09-19 on the BW-up entries themselves, then RE-RUN through
     *    the shipped rule so these are the numbers this configuration actually
     *    produces. The first pass quoted a NO-STOP run against a defaulted-on
     *    stop; verifyBwTrades caught it.
     *
     *    At the 2.5xATR default: crypto +3.60% avg / 60.0% win, equities
     *    +3.25% / 70.8%. With no stop at all: +3.90% / 60.3% and +4.39% / 77.3%.
     *    A flat 5-bar hold gives +2.20% / +2.39%.
     *
     *    Compared AT THE SAME STOP, waiting for overbought is +1.03% on crypto,
     *    far worse, and +3.36% on 10 years of equities, slightly better; with no
     *    stop on either side it is +1.46% and +5.39%, so on equities it wins
     *    outright. The midline is the default because it is the one that is good
     *    in BOTH, and because crypto is the traded market here. See the table in
     *    utils/bbrWaveTrades.ts; every figure is printed by verifyBwTrades. */
    showExits: boolean;
    /** BBR level that closes the trade. 0 = the midline, which is what measured. */
    exitLevel: number;
    /** Stop distance in ATR(14) from the entry close. 0 disables the stop. */
    stopAtr: number;
    /** Hard bar cap so a trade cannot hang open forever. */
    maxHold: number;
    exitColor: string;
    stopColor: string;
    /** Ran out of bars: neither the level nor the stop decided it. */
    timeColor: string;

    /* ── Short entry.
     *
     *    'breakdown' is BBR crossing below 0 with Wave below 0 and price below
     *    its EMA50 — momentum, NOT a mirror of the long. The mirror (both
     *    overbought + BBR turning down) was measured DEAD and has been removed:
     *    −0.41 edge over 10 years of equities, 95% CI [−1.15, +0.29], negative
     *    in both halves of the sample.
     *
     *    Be clear-eyed about what 'breakdown' is too. Crypto by year: 2024
     *    −0.43, 2025 +1.50, 2026 −0.50 — the whole average is one year. Equities:
     *    4 positive years against 6 negative. It is the best short I could find
     *    out of seven designs, and it is still mostly just short exposure. OFF
     *    by default, and it stays a chart mark: nothing here places an order. */
    shortMode: 'off' | 'breakdown';
    shortTargetAtr: number;
    shortStopAtr: number;
    shortMaxBars: number;
    /** HTF curves: draw the still-forming HTF bar (dashed; its value moves). */
    htfShowForming: boolean;
    /** HTF curves: tint the pane where every HTF curve that is on sits beyond
     *  its own oversold / overbought level. A visual cue only. */
    htfConfluence: boolean;
    /** Its OWN opacity. It borrowed relZoneOpacity at first, and an account with
     *  a strong BBR-vs-Wave tint then got a confluence wash that hid the curves. */
    htfConfluenceOpacity: number;
  };
  /** VPA — Volume Spread Analysis signal markers. Only the per-signal show
   *  toggles are user-configurable; all other VSA params use the source
   *  pinescript defaults. Each toggle gates one VSA marker group. */
  vpa: VpaToggles;
  /** LRC signal — close (of a selectable candle type) crossing C-POC / H-POC
   *  top/bottom → BUY/SHORT ★ (C-POC) / ★★ (H-POC). */
  lrc: { candleType: 'Regular' | 'HA' | 'CSH' | 'CSL' | 'CLH' };
  /** HLDiff (R134) — EMA of the Highs + EMA of the Lows of a selectable
   *  candle type (Regular/HA/CSH/CSL/CLH), with a translucent fill between
   *  the two lines. Always computed from RAW candles + the chosen transform,
   *  independent of the chart's display candle type. */
  /** MM (Market Maker) — staircase band from BBR-reversal / Vol-spike source candles. */
  mm: {
    /** Source candle type the BBR/Vol detection + Open/Close body use (independent of
     *  the chart's display candle type). */
    candleType: 'Regular' | 'HA' | 'CLH';
    bullColor: string;
    bearColor: string;
    /** 0..1 band fill opacity. */
    opacity: number;
    /** Draw the chart-timeframe staircase (the original MM). Default true. */
    showLtf: boolean;
    /** Also draw the staircase of the Strategies-mapped HIGHER timeframe. Each
     *  stair starts only once the HTF bar that made it has CLOSED (owner's
     *  choice 2026-09-25) — never across the HTF bar itself, which would show a
     *  level before it existed. Default false. */
    showHtf: boolean;
    /** The HTF staircase's own fill opacity (the borrowed-opacity trap: one
     *  shared value made the two staircases indistinguishable). */
    htfOpacity: number;
  };
  hlDiff: {
    candleType: 'Regular' | 'HA' | 'CSH' | 'CSL' | 'CLH';
    /** One EMA period applied to BOTH the Highs and the Lows. */
    emaPeriod: number;
    /** 'line' = High & Low EMA lines + fill overlaid on the price chart;
     *  'histogram' = High−Low EMA spread bars, always in the shared lower
     *  pane (the spread is not on the price scale, so no overlay option). */
    mode: 'line' | 'histogram';
    highColor: string;
    lowColor: string;
    lineWidth: number;
    fillColor: string;
    /** 0..1 — 0 disables the fill. */
    fillOpacity: number;
    /** Histogram bar colours by EMA slope (vs previous bar): both EMAs
     *  rising / both falling / High rising while Low falls / High falling
     *  while Low rises. */
    histBothRiseColor: string;
    histBothFallColor: string;
    histHiRiseLoFallColor: string;
    histHiFallLoRiseColor: string;
    /** 0..1 — opacity applied to all histogram bars. */
    histOpacity: number;
  };
  /** S/R indicator (R137) — Support/Resistance levels from one or more source
   *  methods, all plotted on the price pane at once. Per-method enables live
   *  under `methods`; styling below is shared across methods. Active level =
   *  deep/prominent, inactive = faint; HTF lines are thicker than CTF. */
  sr: {
    methods: {
      /** HLDiff SR — levels at HLDiff-histogram local peaks (uses the candle
       *  type + EMA period from `hlDiff`). Dark-green peak → Resistance at the
       *  peak bar's high; dark-red peak → Support at the peak bar's low. */
      hlDiff: {
        enabled: boolean;
        /** Plot the chart-timeframe stream. */
        useCurrentTf: boolean;
        /** Plot the Strategies-mapped HTF stream. */
        useHtf: boolean;
        /** Keep at most this many levels per stream (active always kept). */
        maxLevelsPerTf: number;
      };
    };
    resistanceColor: string;
    supportColor: string;
    /** Line width — current TF (thin) vs HTF (thick). */
    ctfWidth: number;
    htfWidth: number;
    /** Opacity for the single active level vs the faint inactive ones. */
    activeOpacity: number;
    inactiveOpacity: number;
    /** Line style: 0=solid, 1=dotted, 2=dashed. */
    lineStyle: number;
    /** Show the "<method> <S/R> · <tf>" text label on each level (drawn at the
     *  RIGHT end of the line). */
    showLabels: boolean;
    /** 'levels' = discrete horizontal levels (each from its formation bar to
     *  the right edge); 'band' = a running channel: stepped lines tracking the
     *  most-recent Resistance / Support at each bar, with a fill between. */
    displayMode: 'levels' | 'band';
    /** Band-mode fill colour + opacity (between the running R and S lines). */
    bandFillColor: string;
    bandFillOpacity: number;
    /** Break / Respect markers (R140) — CLH-body vs HTF-S/R event markers
     *  (confirmed breakout/breakdown, false-breach traps, respect bounce/
     *  rejection). Detection on closed bars, look-ahead-safe. */
    markers: {
      enabled: boolean;
      /** Buffer as a fraction of the (R − S) channel width. */
      bufFrac: number;
      /** `m` — bars to resolve a provisional breach. */
      window: number;
      /** `n` — qualifying closes to confirm a breach. */
      holds: number;
      /** `cd` — bars to suppress re-arm / respect after a false breach. */
      cooldown: number;
      /** `k` — a level "test" must be within this many bars for a respect. */
      testLookback: number;
      showConfirmed: boolean;
      showFalse: boolean;
      showRespected: boolean;
    };
  };
  /** EMAs overlay — 4 independently-configurable EMAs over LinReg(close, 14).
   *  Default periods 7/21/51/200 match the prior hardcoded behaviour; each EMA
   *  has its own enabled toggle so users can hide ones they don't need. */
  emas: {
    ema1: { period: number; enabled: boolean; color: string };
    ema2: { period: number; enabled: boolean; color: string };
    ema3: { period: number; enabled: boolean; color: string };
    ema4: { period: number; enabled: boolean; color: string };
  };
  /** RelStrength — Relative Strength vs a benchmark. Ratio of LinReg(close,14)
   *  of chart symbol divided by LinReg(close,14) of benchmark, normalised to
   *  start at 100. Benchmark auto-resolves from the chart symbol if blank
   *  (crypto→BTCUSD; BTCUSD→ETHUSD; NSE stock→NIFTY 50; NIFTY 50→BANK NIFTY).
   *  R73 — added a configurable EMA of the line; the two lines bound a
   *  translucent band so direction + momentum read at a glance. */
  relStrength: {
    /** Optional override; leave blank to use the auto-resolved benchmark. */
    benchmarkSymbol: string;
    /** Exchange of the benchmark (auto-resolved alongside symbol). */
    benchmarkExchange: string;
    color: string;
    lineWidth: number;
    /** Period of the EMA drawn on top of the RS line. Default 7 — quick
     *  follow that highlights swings without being too noisy. */
    emaPeriod: number;
    /** @deprecated R94 — replaced by directional bandUpColor/bandDownColor.
     *  Retained for back-compat with saved templates. */
    bandFillColor: string;
    /** Opacity for the band fill (0..1). 0 = no fill. */
    bandOpacity: number;
    /** R94 — directional band: greenish when RS line ≥ its EMA. */
    bandUpColor: string;
    /** R94 — directional band: reddish when RS line < its EMA. */
    bandDownColor: string;
    /** R105 — render in the shared lower indicator pane (true, default) or
     *  overlay on the price chart (false). */
    pane: boolean;
  };
  /** ChgN% — rolling N-candle percentage change of close vs close N bars
   *  ago. R73: lookback is now N bars (user-configurable), not the old
   *  ceil(1440/intervalMinutes), so the indicator stays meaningful on
   *  any timeframe (24-bar default = last 24 candles regardless of TF).
   *  Same EMA + band treatment as RelStrength. */
  dayChange: {
    color: string;
    lineWidth: number;
    /** Reference zero-line shown on the indicator scale. */
    showZeroLine: boolean;
    /** Lookback in bars. Default 24 — what was "last 24 hours" on a 1H
     *  chart now generalises to "last 24 bars" on any chart. */
    lookbackBars: number;
    /** Period of the EMA drawn over the change line. Default 7. */
    emaPeriod: number;
    /** @deprecated R94 — replaced by directional bandUpColor/bandDownColor. */
    bandFillColor: string;
    bandOpacity: number;
    /** R94 — directional band: greenish when ChgN% line ≥ its EMA, reddish below. */
    bandUpColor: string;
    bandDownColor: string;
    /** R105 — render in the shared lower indicator pane (true, default) or
     *  overlay on the price chart (false). */
    pane: boolean;
  };
  /** MACD (Round 94) — classic MACD mimicking CM_MacD_Ult: macd = EMA(fast) −
   *  EMA(slow); signal = SMA(macd, signalLength); plotted as MACD + Signal
   *  lines with a directional band (greenish when MACD ≥ Signal, reddish below).
   *  Histogram/dots/zero-line intentionally omitted. */
  macd: {
    fastLength: number;
    slowLength: number;
    signalLength: number;
    macdColor: string;
    signalColor: string;
    bandUpColor: string;
    bandDownColor: string;
    bandOpacity: number;
    /** R105 — render in the shared lower indicator pane (true, default) or
     *  overlay on the price chart (false). */
    pane: boolean;
  };
  /** Synthiya — the UNDERLYING's put-call ratio, ATM implied volatility and
   *  relative strength as three staircase lines in the lower pane. Each step is
   *  coloured UP when it rises against the previous step, DOWN when it falls.
   *
   *  This block has to exist for the dialog's Synthiya section to survive a
   *  reload: `resolvePayload` copies only keys that are present here, so while
   *  it was missing every one of these nine fields was written by the dialog,
   *  stored, and then silently dropped on the way back — the same failure the
   *  note above `dbcr` records. The values below are the ones the dialog and
   *  TradingChart already fall back to independently, so adding them changes
   *  nothing on screen; it only makes a change the user makes stick. */
  synthiya: {
    pcrUpColor: string; pcrDownColor: string; pcrWidth: number;
    ivUpColor: string; ivDownColor: string; ivWidth: number;
    rsUpColor: string; rsDownColor: string; rsWidth: number;
  };
  /** Historical Order Flow overlay — per-candle bias from per-minute taker buckets
   *  (delta_trade_buckets, 3-day retention), drawn as a colour strip below the
   *  candles and coloured by the bar's flow STATE.
   *
   *  The palette is deliberately not configurable. Round 67h (ad3d522) replaced
   *  the original 5-colour + triangle-marker design with a 7-state ribbon whose
   *  colours live in `FLOW_STATE_COLORS` (TradingChart.tsx). That commit removed
   *  every read of `showTriangles`, `showAbsorptionBadges` and the five colour
   *  fields but left them declared here, so for six rounds this block advertised
   *  seven settings that nothing read — and five colours could not have described
   *  a seven-state ribbon anyway. They are gone; `showStrip` is the whole of it,
   *  and it is the only control the dialog has ever offered. */
  flow: {
    showStrip: boolean;
  };
  /** OverView trend meter (Round 92) — the on-chart dashboard summarising 7
   *  indicators into a -100..+100 score per timeframe, shown as a speedometer.
   *  Weights are per-indicator (equal by default); thresholds set the zone
   *  boundaries; colors seed from the global theme/overviewColors palette. */
  overview: {
    weights: {
      ema: number; avgCloud: number; cpr: number; cpoc: number;
      hpoc: number; relStrength: number; chgN: number; macd: number; jhm: number;
      wrPct: number;
    };
    /** |score| boundary for Strong vs Probable zones. */
    strongThreshold: number;
    probableThreshold: number;
    zoneColors: {
      strongShort: string; probableShort: string; neutral: string;
      probableBuy: string; strongBuy: string;
    };
    upColor: string; downColor: string; neutralColor: string;
    /** Opacity of the gauge arc fill (0..1). */
    gaugeOpacity: number;
    /** Opacity of the "Bkground" chart tint (0..1). (Legacy — superseded by the
     *  `bkground` group below; kept for back-compat with saved templates.) */
    bgTintOpacity: number;
  };
  /** "Bkground" indicator (R110) — per-candle vertical background tint on the
   *  price pane: bullish when the combined TF+HTF trend score > 0, bearish ≤ 0.
   *  (Replaces the old single whole-chart tint keyed off the last candle.) */
  bkground: {
    /** R119 — background-color computation method:
     *   • 'ctf' (default, legacy): sign of the combined chart-TF + HTF trend
     *     score → bull/bear tint (bullColor / bearColor below).
     *   • 'htf': paint each chart-TF candle with the HTF bar's RockBand zone
     *     colour (5 OverView zones), mapped by the candle's start time — i.e.
     *     mirror the HTF trend on the current chart's background.
     *   • 'clhMid': the slope of the CLH Band's HIGHER-TIMEFRAME midline —
     *     bullColor where it is falling, bearColor where it is rising, the same
     *     reading as the midline's own colours. Unlike 'htf' this one is
     *     strictly CAUSAL: the level comes from the last HTF bar that CLOSED
     *     before each candle, never the HTF bar containing it. It does not need
     *     the CLH Band overlay to be switched on.
     *   • 'stack': price, its smoothed body and the higher-timeframe context
     *     band read as one ladder — bull when close > mid > band top, bear when
     *     close < mid < band bottom, NEUTRAL otherwise. The neutral case is the
     *     point: while price works inside the higher-timeframe body there is no
     *     regime to report. See utils/regimeStack.ts for the rule and for why
     *     this is drawn context rather than something to gate on. */
    method?: 'ctf' | 'htf' | 'clhMid' | 'stack';
    /** 'stack' only — which candle type supplies the middle rung and the band.
     *  Its OWN setting rather than the chart's, the same way bbrWave.srcCandle
     *  and mm.candleType are: switching the chart to Heikin-Ashi to read the
     *  trend should not silently redefine an indicator underneath it.
     *  'Regular' drops the middle rung, because with no smoothing it would be
     *  the same number as the close and the rule could never fire. */
    srcCandle?: 'Regular' | 'HA' | 'CLH' | 'CLN';
    bullColor: string;
    bearColor: string;
    /** 'stack' only — the third state. The other methods are binary. */
    neutralColor?: string;
    /** Fill opacity of the background zones (0..1). */
    opacity: number;
  };
  /** "Funding" indicator — Delta perpetual funding rate (% per 8h). Lower-pane
   *  baseline line, sign-coloured around zero. */
  funding?: {
    /** Colour when funding > 0 (longs pay shorts). */
    positiveColor: string;
    /** Colour when funding < 0 (shorts pay longs). */
    negativeColor: string;
    lineWidth: number;
    /** Render in the shared lower pane (true) or overlaid on price (false). */
    pane?: boolean;
    /** Funding Signals (contrarian crowding + reversion) — markers on the pane. */
    signals?: boolean;
    /** EMA smoothing length applied to funding before signal detection. */
    emaLength?: number;
    /** Rolling lookback (candles) for the adaptive z-score bands. */
    lookback?: number;
    /** Band width in std-devs (mean ± k·σ). */
    k?: number;
    /** Draw the faint upper/lower band lines on the pane. */
    showBands?: boolean;
    /** Line thickness (px) of the z-score band lines. */
    bandWidth?: number;
  };
  /** "BOLev" — breakout-level projector from Daily (BOLev_D) / Weekly (BOLev_W)
   *  CLH candles. Shared styling for both. */
  bolev: {
    buyColor: string;
    shortColor: string;
    /** Opacity of the breakout zone band (0..1). */
    bandOpacity: number;
    lineWidth: number;
    /** How many completed candles feed the level (default 2). */
    lookback: number;
    /** Item 4b — translucent channel fill between the BUY & SHORT levels. */
    channelFill?: boolean;
    channelColor?: string;
    channelOpacity?: number;
  };
  /** "Blast" — Inside (smoothed HA) / Engulfing (Regular) detection on Daily &
   *  Weekly draws a High/Low band for the next 2 periods; a fresh C-POC
   *  breakout while a band is active fires BUY/SHORT. Per-period colour,
   *  opacity, and on/off toggles. */
  blast: {
    dailyColor: string;
    dailyOpacity: number;   // 0..1
    weeklyColor: string;
    weeklyOpacity: number;  // 0..1
    /** Staircase bull/bear fill colours (band steps up = bull, down = bear). */
    bullColor: string;
    bearColor: string;
    /** Show the Daily band (on chart intervals ≤ 1D). Default on. */
    showDaily: boolean;
    /** Show the Weekly band (on chart intervals ≤ 1W). Default on. */
    showWeekly: boolean;
    /** Show the on-chart BUY/SHORT breakout signals. Default on. */
    showSignals: boolean;
  };
  /** "Trend" band (Round 98) — overlays the chart-TF and HTF OverView trend
   *  scores (−100..+100) on the price pane as two lines (thin TF, thick HTF). */
  trend: {
    /** Line colour for both the TF and HTF lines. */
    color: string;
    /** Line opacity (0..1) applied to both lines. */
    opacity: number;
    /** Chart-timeframe line thickness (thin). */
    tfLineWidth: number;
    /** Higher-timeframe line thickness (thick). */
    htfLineWidth: number;
    /** Smoothing applied to the raw step score so the lines read smoothly
     *  (Round 102): 'linreg' (LSMA), 'hull' (Hull MA), 'ema', or 'none' (raw). */
    smoothing: 'none' | 'linreg' | 'hull' | 'ema';
    /** Smoothing look-back period (bars). */
    smoothingPeriod: number;
    /** Round 104 — which line groups to draw. */
    showIndividual: boolean;   // the TF (thin) + HTF (thick) lines
    showCombined: boolean;     // the combined line = TF score + HTF score (−200..+200)
    showHma: boolean;          // the Hull MA of the combined line
    /** Combined line colour + thickness. */
    combinedColor: string;
    combinedLineWidth: number;
    /** Hull-MA line colour + thickness (drawn thicker by default). */
    hmaColor: string;
    hmaLineWidth: number;
    /** Hull-MA look-back period (bars). */
    hmaPeriod: number;
    /** Band filling the zone between the combined line and its HMA:
     *  positive colour when combined ≥ HMA, negative when below, + opacity.
     *  (R106: bandUpColor/bandDownColor superseded by the shared bull/bear
     *  palette below; kept for back-compat with saved templates.) */
    bandUpColor: string;
    bandDownColor: string;
    bandOpacity: number;
    /** R105 — render in the shared lower indicator pane (true, default) or
     *  overlay on the price chart (false). */
    pane: boolean;
    /** R106 — one shared palette used across the whole indicator: Combined line
     *  (bull above HMA / bear below), HMA line (bull rising / bear falling /
     *  neutral flat), the Combined↔HMA band, the above/below-zero area, the
     *  vertical regime zones and the divergence markers. */
    bullColor: string;
    bearColor: string;
    neutralColor: string;
    /** Auto-detected price↔indicator divergence markers (regular + hidden). */
    showDivergence: boolean;
    /** Opacity of the above-zero (bull) / below-zero (bear) area shade. */
    zeroAreaOpacity: number;
    /** Opacity of the full-height vertical background regime zones. */
    zoneOpacity: number;
  };
  /** JHM (JSR Hilega-Milega) oscillator (Round 103) — RSI(src) with a VWMA and
   *  EMA of that RSI, plotted 0–100 with a 50 midline, bull/bear zone shading and
   *  an EMA/VWMA fill. Only these inputs are user-editable; line/fill/dot colors
   *  use fixed theme-safe defaults. */
  jhm: {
    rsiLength: number;
    /** RSI source: raw close, or a linear-regression-smoothed close. */
    source: 'close' | 'linregClose';
    vwmaLength: number;
    emaLength: number;
    bullShadeColor: string;
    bullShadeOpacity: number;   // 0..1
    bearShadeColor: string;
    bearShadeOpacity: number;   // 0..1
    /** R105 — render in the shared lower indicator pane (true, default) or
     *  overlay on the price chart (false). */
    pane: boolean;
  };
  /** WR% (Round 116) — paired Williams %R EMAs (fast + slow) on a fixed
   *  -100..0 strip. Crossover of fastEMA over slowEMA = bull (band shaded
   *  bull colour); below = bear. OB/OS at -20/-80 are fixed visual guides. */
  wrPct: {
    source: 'close' | 'linregClose';
    fastLength: number;
    fastEma: number;
    slowLength: number;
    slowEma: number;
    fastLineColor: string;
    slowLineColor: string;
    bullBandColor: string;
    bullBandOpacity: number;   // 0..1
    bearBandColor: string;
    bearBandOpacity: number;   // 0..1
    pane: boolean;
  };
  /** Vol — Weighted Volume (volume × close). Lower pane (default) showing ONLY
   *  spike bars (raw wVol > slow × 2) plus Fast(3)/Slow(21) EMAs of LinReg(7)-
   *  smoothed wVol with a bull/bear band between them. LinReg/EMA lengths and
   *  spike multiplier are hardcoded; only display is configurable. */
  vol: {
    showEmas: boolean;
    bandBullColor: string;
    bandBearColor: string;
    bandOpacity: number;       // 0..1
    bullSpikeColor: string;
    bearSpikeColor: string;
    fastColor: string;
    slowColor: string;
    pane: boolean;             // true = lower pane (default)
  };
  /** AV_Band — Anchored Volume Band (price pane). HMAs (fast/slow) of an
   *  Effective Line derived from WVol spike zones + chart-TF OverView score. */
  avBand: {
    fastPeriod: number;        // default 14
    slowPeriod: number;        // default 100
    lineWidth: number;         // 1..4
    /** Effective Line boundary-pick rule by chart-TF OverView score:
     *  'logic1' (default) → score>20 = Zone HIGH, score<-20 = Zone LOW, else mid.
     *  'logic2'           → score>20 = Zone LOW,  score<-20 = Zone HIGH, else mid. */
    effectiveMode: 'logic1' | 'logic2';
    /** Line drawing style for the Fast/Slow HMA lines:
     *  'staircase' (default) → stepped; 'line' → straight segments. */
    lineMode?: 'staircase' | 'line';
    showFast: boolean;         // plot the Fast HMA line (default on)
    showSlow: boolean;         // plot the Slow HMA line (default on)
    showBand: boolean;         // fill the band between Fast & Slow (default on)
    showEffective: boolean;    // plot the stepped Effective Line (default off)
    effectiveWidth: number;    // 1..4, Effective Line thickness
    fastColor: string;
    slowColor: string;
    effectiveColor: string;
    bandBullColor: string;
    bandBearColor: string;
    bandOpacity: number;       // 0..1
    /** AV_Trend Band — a stepped support/resistance channel off the SLOW HMA:
     *  a fresh REGULAR close above it → Long, mark the lowest low since the last
     *  slow-HMA touch (lower edge); a fresh close below → Short, mark the highest
     *  high since the last touch (upper edge). Filled green while Long / red while
     *  Short. Off by default. */
    showTrendBand?: boolean;
    trendBullColor?: string;
    trendBearColor?: string;
    trendOpacity?: number;     // 0..1
  };
  /** BB — Bollinger Bands (price pane). Four computation variants share one set
   *  of look controls (colors, fill, line style, thickness, middle toggle). */
  bollinger: {
    /** 'traditional' = SMA(displayClose, period) ± mult·σ (the classic; unchanged).
     *  'liner'        = EMA of CLH close ± mult·σ of CLH close.
     *  'customLiner'  = mid EMA(CLH close); upper EMA(bodyTop)+mult·σ(bodyTop),
     *                   lower EMA(bodyBottom)−mult·σ(bodyBottom) — asymmetric.
     *  'customLinerWeighted' = same as customLiner but VWMA + volume-weighted σ. */
    variant: 'traditional' | 'liner' | 'customLiner' | 'customLinerWeighted';
    period: number;            // moving-average / σ window (default 20)
    stdMult: number;           // σ multiplier (default 2)
    showMiddle: boolean;       // plot the middle (basis) line (default on)
    /** Fill the area between upper & lower with a translucent color. */
    fillEnabled: boolean;      // default on
    fillColor: string;
    fillOpacity: number;       // 0..1
    /** Line drawing style: 'line' (straight, default) or 'staircase' (stepped). */
    lineMode: 'line' | 'staircase';
    lineWidth: number;         // 1..4
    bandColor: string;         // upper & lower edge color
    middleColor: string;       // middle (basis) line color
  };
  /** VOB — Volume-Trend Order Block Engine. Custom supertrend cloud + volume-split
   *  order-block boxes + retest crosses. All inputs mirror the source Pine script. */
  vob: {
    /** Volatility SMA length for the supertrend band (SMA of high−low). */
    stLen: number;
    /** Supertrend band multiplier. */
    stMult: number;
    /** Draw the cloud fill between price and the trend-stop line. */
    showFill: boolean;
    cloudBullColor: string;
    cloudBearColor: string;
    /** Swing-pivot strength (bars each side) for order-block detection. */
    pivotLen: number;
    /** Bullish / bearish order-block fill+border base colours. */
    bullColor: string;
    bearColor: string;
    /** Order-block "Sell %/Buy %" label colour. */
    txtColor: string;
    /** Delete an order block once price fully breaks through it. */
    deleteOnBreak: boolean;
    showBullRetest: boolean;
    bullVolPct: number;       // 0..100 — min Buy % to allow a bullish retest
    bullSigColor: string;
    showBearRetest: boolean;
    bearVolPct: number;       // 0..100 — min Sell % to allow a bearish retest
    bearSigColor: string;
  };
  /** FNO indicator (rebuilt 2026-11-03) — ONE expiry's option market per candle, as of
   *  its close: OI walls / max pain / γ flip on the price pane, PCR / IV / OI change in
   *  their own panes. The rolling tier (exactly one of tierD/W/M) is the default; a date
   *  can be pinned from the chart legend. Delta BTC/ETH + NSE/MCX names. */
  fno: {
    tierD: boolean; tierW: boolean; tierM: boolean;   // rolling expiry tier (single-select)
    showSR: boolean;        // call / put OI walls
    srCount?: number;       // walls per side (1 = the biggest only)
    showMaxPain: boolean;   // Max Pain running line
    showGexFlip: boolean;   // zero-gamma flip running line
    showWalls: boolean;     // top ± GEX walls (signed colours)
    wallCount?: number;
    showOiRange?: boolean;  // fill between the call wall and the put wall
    showExpMove?: boolean;  // spot ± ATM straddle band (the move the market prices)
    showRegime?: boolean;   // γ-regime background tint (+γ pinning / −γ trending)
    showRolls?: boolean;    // mark the candle where a rolling tier moved to the next expiry
    showGaps?: boolean;     // shade candles inside a collector outage
    showPcr?: boolean; showPcrEma?: boolean; pcrEmaLength?: number;
    showIv?: boolean; showIvEma?: boolean; ivEmaLength?: number;
    showOi?: boolean;       // OI pane
    oiMode?: 'net' | 'total';
    lineMode?: 'historical' | 'latest';
    oiRangeColor?: string; expMoveColor?: string;
    pcrColor?: string; pcrEmaColor?: string; ivColor?: string; ivEmaColor?: string;
    days: number;           // history window for the running lines
    srColor: string;             // legacy single S/R colour (kept for back-compat)
    srSupportColor: string;      // OI support lines (bullish)
    srResistanceColor: string;   // OI resistance lines (bearish)
    maxPainColor: string;
    gexFlipColor: string;
    wallPosColor: string;   // positive-gamma wall (long gamma)
    wallNegColor: string;   // negative-gamma wall (short gamma)
    payoffTpColor: string;  // option-basket take-profit underlying level line
    payoffSlColor: string;  // option-basket stop-loss underlying level line
  };
  /** DbCrZone vertical regime zones (drawn in TradingChart from settings.dbcr). */
  dbcr: {
    debitColor: string;   // trending / premium-expansion regime
    creditColor: string;  // range / premium-decay regime
    opacity: number;
  };
  /** Bhairav chart strategy — which candle transform drives the signals and the
   *  notional the simulation badge trades with. */
  bhairav: {
    /** FOLLOW = use the chart's selected candle type (HA/CLH; others → CLH). */
    candleType: 'FOLLOW' | 'HA' | 'CLH';
    investInr: number;
  };
  /** OM_Simple chart strategy — the CLN regression length its signals are
   *  decided by. Deliberately SEPARATE from candle.clnLength (the length the
   *  chart DRAWS, default 14): measured over 10 majors 2024-03 to 2026-09 at 7H
   *  with 0.20% round-trip costs, the rule at 14 fired 22.5 times a month per
   *  symbol and returned -0.125%/trade, at 50 it fired 11.3 and returned
   *  +0.045%. So the strategy defaults to 50 and the overlay WARNS when the
   *  drawn length differs, rather than marking colour changes that are not on
   *  screen. */
  omSimple: {
    clnLength: number;
    /** Skip a flip printed on thin participation. Default false — see
     *  utils/strategies/omSimpleEngine.ts for the measurement behind that. */
    suppressThinVolume: boolean;
    /**
     * W (watch) and C (continuation) marks. CHART ANNOTATIONS ONLY — they raise
     * no alert, write no scan row and do not change what OM_Simple trades.
     * Thresholds are 0-100 on the pressure score; the shipped defaults are
     * calibrated, NOT the Pine reference's 55/70, which sit at the 98th and
     * 96th percentile of this data. See utils/strategies/omSimplePressure.ts.
     */
    showWatch: boolean;
    showContinuation: boolean;
    watchThreshold: number;
    continuationThreshold: number;
  };
  /**
   * Vajra chart strategy — the shipped BW long PLUS the mapped higher timeframe
   * having extended at least `minHtfRun` bars in its current direction.
   *
   * At an oversold entry that direction is almost always DOWN (1,019 of 1,025
   * occurrences on 4H), so the gate means "the higher timeframe is falling too,
   * and has been for a while" — a deeper-oversold condition across two
   * timeframes, not a trend filter.
   *
   * Measured over 47 crypto symbols and 487,000 four-hour bars, 2019-2026, net
   * of 0.20% round-trip costs: ungated +0.10% a trade at 53.7% win; gated
   * +0.93% at 56.9% win on 1,054 trades, 3.32 per symbol per year. It beat all
   * 4,000 random selections of the same size, won 4 of 4 walk-forward folds,
   * improved 36 of 47 symbols and beat the ungated rule in seven of eight years.
   *
   * `minHtfRun` IS the edge and the response is monotone in it — 0/1/2/3/4 give
   * +0.10 / +0.19 / +0.93 / +1.85 / +2.84 per trade. The default is 2 because 2
   * is the value that was pre-registered and cleared multiple-testing control;
   * 3 and 4 look better and were not in the tested family.
   *
   * `stopAtr` stays at 2.5. The wider 4.0 stop that wins on DAILY bars does not
   * carry over, because a held trade blocks the next signal here, so a wider
   * stop costs entries. Every figure is reproduced by
   * `backend/src/scripts/swing/verifyVajra.ts`.
   */
  vajra: {
    bbOs: number;
    wvOs: number;
    requireTurn: boolean;
    minHtfRun: number;
    exitLevel: number;
    stopAtr: number;
    maxHold: number;
    /** Percent of account risked between entry and stop. Sizing guidance shown
     *  on the chart only — nothing here places an order. */
    riskPct: number;
  };
  /**
   * CLH Band — the real price at which the NEXT CLH body breaks out of the
   * current one. Solved in closed form from the CLH transform itself (every
   * step of it is linear and no min/max touches the close), so the level is
   * exact rather than fitted: round-tripping a solved level back through
   * `transformCandles` lands on its target to 8e-13 of price.
   *
   * READ THE LINES AS ROLE-SWITCHING, not as fixed triggers. The upper line
   * sits BELOW spot on about half of all bars, because when CLH is already
   * rising the next body clears the old body top even at flat price. On that
   * side it is "where the up-move would fail" — a trailing support — rather
   * than a breakout trigger. The lower line behaves the same way in reverse.
   * `showFlip` adds the one level that is always a genuine flip: where the next
   * body changes colour.
   *
   * The levels are often far from price (a quarter of bars beyond ~10 ATR) —
   * that is real, CLH moves only 2.79% of a price move, so a strong trend takes
   * a big move to break. The band is drawn as a canvas primitive, which does
   * not drive the price axis, so a distant level clips off-screen instead of
   * flattening the candles; `showLabels` keeps its value readable anyway.
   *
   * The CLH regression length is NOT configurable here: it is fixed at 14
   * inside candleTransforms.ts, and a band drawn on a different length would
   * stop describing the candle it is named after.
   */
  clhBand: {
    /** Levels for the chart's own timeframe. */
    showTf: boolean;
    /** Levels for the mapped higher timeframe (same ladder as Strategies). */
    showHtf: boolean;
    /** The two band EDGES. Independent of the middle line, so the middle line
     *  can be read on its own — the least cluttered way to use this, since the
     *  edges sit about 36x the CLH body apart. */
    showBandLines: boolean;
    /** The colour-flip level, i.e. the middle line. Off by default — two lines
     *  were what was asked for. */
    showFlip: boolean;
    showFill: boolean;
    showLabels: boolean;
    upperColor: string;
    lowerColor: string;
    /** The middle line takes its colour from ITS OWN slope, not from price:
     *  a FALLING midline is bullish (the level the next body must beat to turn
     *  green is coming down to meet price), a RISING one is bearish. A flat
     *  step carries the previous direction - see stepDirections in clhBand.ts.
     *
     *  These replaced flipAboveColor / flipBelowColor, which named the old
     *  price-relative rule. The old keys are still declared below so an
     *  existing template keeps its customised colours (resolvePayload drops any
     *  group key the defaults do not declare) and so the read can fall back to
     *  them; nothing writes them any more. */
    flipFallColor: string;
    flipRiseColor: string;
    /** @deprecated superseded by flipFallColor — read as its fallback. */
    flipAboveColor: string;
    /** @deprecated superseded by flipRiseColor — read as its fallback. */
    flipBelowColor: string;
    fillBullColor: string;
    fillBearColor: string;
    fillOpacity: number;
    /** Line width for this chart's own timeframe. */
    lineWidth: number;
    /** Line width for the higher-timeframe pair, kept separate so a busy chart
     *  can thin the HTF lines without thinning the ones being traded. */
    lineWidthHtf: number;
    /** 0 solid, 1 dotted, 2 dashed — lightweight-charts LineStyle. */
    lineStyle: number;
    /** Line style for the higher-timeframe lines, kept separate the same way
     *  lineWidthHtf is. Defaults to dashed, which is how the HTF half has
     *  always been drawn; set it solid to read the HTF midline on its own. */
    lineStyleHtf: number;
    /** HTF band opacity as a fraction of the TF band's, so both can be on at
     *  once and still be told apart. */
    htfOpacityScale: number;
  };
  /**
   * CLN_Break - the breakout levels of the CLN linear-regression bodies.
   *
   * buyLevel   = max of the previous `lookback` LR body highs, + bufferAtr x ATR
   * shortLevel = min of the previous `lookback` LR body lows,  - bufferAtr x ATR
   *
   * MEASURED GEOMETRY, because these dials do not do what they look like they do.
   * The channel between the two levels is NARROWER THAN ONE AVERAGE BAR'S RANGE
   * (0.72-0.78 x ATR), so at least one level is breached on 95-96% of bars and
   * BOTH are breached on 10-18% - those bars are resolved pessimistically, and
   * the rule flips roughly every 5.6 bars at EVERY timeframe.
   *   - `bufferAtr` 0.5 takes the both-breached rate from 13.5% to 1.0% and cuts
   *     flips by 28%. It is the dial that matters.
   *   - `lookback` 10 widens the channel to 2.07 x ATR.
   *   - `clnLength` 50 NARROWS it to 0.43 x ATR - longer regression, thinner
   *     bodies. The opposite of the intuition, which is why it is exposed.
   * Every figure here is recomputed by `npm run verify:cln-break`.
   *
   * There is NO regression length here. The rule reads `candle.clnLength`, the
   * length the chart DRAWS, so its levels are always built from the bodies on
   * screen. A separate length (removed 2026-09-24) put marks on bars that had
   * not breached the bodies the owner was looking at.
   */
  clnBreak: {
    /** How many previous LR bodies form each level. Default 3, as specified. */
    lookback: number;
    /** Widen both levels by this many ATR(14). 0 = the literal rule. */
    bufferAtr: number;
    /** 'touch' = intrabar high/low against the level, which is what a resting
     *  stop order does; 'close' = only a bar closing beyond it. A close can
     *  never breach both levels, so 'close' has no ambiguous bars at all. */
    breach: 'touch' | 'close';
    /** Take only the side agreeing with the previous bar's LR direction, and
     *  stay flat otherwise. Off = the always-in-the-market rule. */
    requireLrAgree: boolean;
    /** Stop in ATR multiples; 0 = none, which is how the rule was measured. A
     *  stop makes the system not always-in and is a risk choice, not a returns
     *  one. */
    stopAtr: number;
    /** Draw the two stepped level lines. The marks come from the CLN_Break
     *  strategy chip; this is the `clnBreakLevels` overlay. */
    showLevels: boolean;
    buyColor: string;
    shortColor: string;
    lineWidth: number;
    /** 0 solid, 1 dotted, 2 dashed - lightweight-charts LineStyle. */
    lineStyle: number;
  };
  /**
   * Gandiv (strategy chip) — HTF band breakout with a fixed stop and a chained
   * stop-and-reverse. See utils/strategies/gandiv.ts.
   *
   * There is NO regression length here: the band is built at `candle.clnLength`,
   * the length the chart draws (the CLN_Break lesson). Every key below is written
   * by the dialog, so every key must be declared here or resolvePayload drops it.
   */
  gandiv: {
    /** The candle whose close is judged against the band. Prices stay real. */
    source: 'Regular' | 'HA' | 'CLH' | 'CLN';
    /** Draw the band as the rule saw it (as-known, not the final HTF body). */
    showBand: boolean;
    showEntry: boolean;
    showSl: boolean;
    /** "E 654.59" / "SL 620.10" at the start of each trade's lines. */
    showLabels: boolean;
    bandUpColor: string;
    bandDownColor: string;
    /** Fill between the band's two lines, coloured by its direction like the
     *  HTF Band chip. 0 = lines only. Its own key, not the HTF Band's. */
    bandOpacity: number;
    longColor: string;
    shortColor: string;
    slColor: string;
    lineWidth: number;
  };
  /**
   * LR_Cross (strategy chip) — a closed candle crosses the LR body's top/bottom.
   * Chart marks use THIS source; the Strategies scan uses the strategy's own
   * setting (owner, 2026-11-01). No length here: it is `candle.clnLength`.
   */
  lrCross: {
    source: 'Regular' | 'HA';
  };
  /**
   * HTF Band (indicator chip, 2026-09-25) — the CLN higher-timeframe context
   * band: the Strategies-mapped HTF's CLN regression bodies as a ribbon behind
   * the candles, coloured by each HTF bar's own LR direction (candle rising /
   * falling colours). It used to be a switch under Chart Settings → CLN; the
   * on/off is now the chip, and these are its settings.
   */
  htfBand: {
    /** Carry the ribbon through the still-forming HTF bar (lighter, dashed). */
    showForming: boolean;
    /** Fill opacity, 0.02–0.40. */
    opacity: number;
  };
  /**
   * "Important fraction" (2026-09-30) — every candle type (Reg / HA / CLH / CLN) is
   * built from the LAST 1/denominator of each bar's time: O/H/L/V from that window,
   * the real close kept (utils/candleFraction.ts). 1 = normal candles, exactly as
   * before. DISPLAY ONLY: indicators, strategies, scans, alerts, orders and P&L
   * keep reading real bars.
   */
  candleFraction: {
    /** 1 (off) or 2–10: the last 1/N of each bar. */
    denominator: number;
  };
  /**
   * P-Zone chip (2026-10-07) — the Dynamic Profit Zone drawn as a REFERENCE:
   * Zone 1 / Zone 2 = the last closed HTF bar's HTF-Band edge ± m1 / m2 × the
   * mean true range of the last 50 chart bars (utils/profitZone.ts). Not a tested
   * signal (docs/EXIT-ARCHITECTURE-STUDY.md); nothing that decides reads it.
   */
  profitZone: {
    /** Zone 1 distance in ATR(50). Default 2.5 (the study's pre-registered value). */
    m1: number;
    /** Zone 2 distance in ATR(50). Default 4. */
    m2: number;
    color: string;
    /** Fill between the zones, 0–0.40. */
    fillOpacity: number;
    lineWidth: number;
    /** Let the zones widen the price scale so they stay on screen. */
    fitScale: boolean;
  };
  /** When true, symbol-specific saved preferences override the page's current
   *  interval/candletype/overlays on load. When false, page values always win. */
  usePerSymbolSaved: boolean;
}

export interface ChartSettingsTemplate {
  id: number;
  userId: number;
  name: string;
  isActive: boolean;
  payload: Partial<ChartSettingsPayload>;
  createdAt: string;
  updatedAt: string;
}

/* ── Per-layout, PER-PANE "remembered zoom" (visible candle count) ────────
   Replaces the old fixed layoutCandleCount setting. Each pane now OPENS at the
   candle count the user last zoomed IT to (captured live as they scroll),
   persisted per-device in localStorage. `layoutCandleCount` in the payload is
   retained only as a back-compat field / initial seed for older templates.

   WHY PER PANE, not per layout.

   This used to store ONE number per layout, handed to every pane in it, and only pane 0
   ever reported a zoom back (`onVisibleBarsChange` was wired on pane 0 alone). In the
   owner's 2-split — 8H on the left, 15m on the right — that meant the 15m pane could never
   keep "ample candles": its own zoom was never recorded, and every re-fit re-applied the
   number the 8H pane had written. Two panes showing two timeframes want two answers, and
   the quad option panes want their own again. */
export type LayoutMode3 = 'single' | 'split' | 'quad' | 'workspace';
export const VISIBLE_BARS_KEY = 'chart_visibleBars_v1';
/** Panes each layout actually renders. Indexes below are always within this. */
export const LAYOUT_PANE_COUNT: Record<LayoutMode3, number> = { single: 1, split: 2, quad: 4, workspace: 1 };
export const DEFAULT_VISIBLE_BARS: Record<LayoutMode3, number> = { single: 300, split: 150, quad: 130, workspace: 160 };

export type VisibleBarsMap = Record<LayoutMode3, number[]>;

/** Round to an integer ≥ 20 (and ≤ 2000); returns null if not a usable count. */
export function clampVisibleBars(n: unknown): number | null {
  const x = Math.round(Number(n));
  if (!Number.isFinite(x) || x < 20) return null;
  return Math.min(2000, x);
}

const LAYOUTS: LayoutMode3[] = ['single', 'split', 'quad', 'workspace'];

/** Fill a layout's pane array, accepting the OLD scalar shape as the seed for every pane. */
function paneArrayFor(layout: LayoutMode3, stored: unknown): number[] {
  const fallback = DEFAULT_VISIBLE_BARS[layout];
  const count = LAYOUT_PANE_COUNT[layout];
  // Old shape: a single number for the whole layout. Seed every pane from it so an
  // existing user's remembered zoom survives the upgrade instead of snapping to defaults.
  const scalar = Array.isArray(stored) ? null : clampVisibleBars(stored);
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    const v = Array.isArray(stored) ? clampVisibleBars(stored[i]) : null;
    out.push(v ?? scalar ?? fallback);
  }
  return out;
}

export function readVisibleBars(): VisibleBarsMap {
  let raw: any = null;
  try { raw = JSON.parse(localStorage.getItem(VISIBLE_BARS_KEY) || 'null'); } catch { /* ignore */ }
  const out = {} as VisibleBarsMap;
  for (const l of LAYOUTS) out[l] = paneArrayFor(l, raw && typeof raw === 'object' ? raw[l] : undefined);
  return out;
}

/** The remembered count for ONE pane. Out-of-range indexes fall back to pane 0. */
export function readVisibleBarsFor(layout: LayoutMode3, pane: number): number {
  const arr = readVisibleBars()[layout];
  const i = Number.isInteger(pane) && pane >= 0 && pane < arr.length ? pane : 0;
  return arr[i];
}

/** Persist the remembered visible-candle count for one pane (no-op if unchanged). */
export function writeVisibleBars(layout: LayoutMode3, pane: number, bars: number): void {
  const v = clampVisibleBars(bars);
  if (v == null) return;
  const i = Number.isInteger(pane) && pane >= 0 && pane < LAYOUT_PANE_COUNT[layout] ? pane : 0;
  try {
    const cur = readVisibleBars();
    if (cur[layout][i] === v) return;
    cur[layout] = cur[layout].slice();
    cur[layout][i] = v;
    localStorage.setItem(VISIBLE_BARS_KEY, JSON.stringify(cur));
  } catch { /* ignore */ }
}

/** Forget all remembered zooms — next open of each layout uses DEFAULT_VISIBLE_BARS. */
export function resetVisibleBars(): void {
  try { localStorage.removeItem(VISIBLE_BARS_KEY); } catch { /* ignore */ }
}

export const DEFAULT_CHART_SETTINGS: ChartSettingsPayload = {
  version: 1,
  candle: {
    risingColor: '#26a69a',
    fallingColor: '#ef5350',
    borderMatchesFill: true,
    wickMatchesFill: true,
    showWick: true,
    clnLength: 14,
    clnVolumeAccent: false,
    lrGhost: true,
    clnHtfBand: false,
    clnHtfBandOpacity: 0.12,
    clnHtfBandForming: true
  },
  layoutCandleCount: { single: 300, split: 150, quad: 130 },
  liveUpdateSec: 5,
  crosshairTooltip: true,
  cpr: {
    opacity: 0.15,
    showMidline: false,
    risingColor: '#26a69a',    // teal/green — same family as candle rising
    fallingColor: '#ef5350',   // red — same family as candle falling
    neutralColor: '#fbc02d',   // amber — distinct from rising/falling
    showProjection: true,
  },
  poc: {
    opacity: 0.30,
    risingColor: '#1e88e5',    // blue — distinct from CPR's teal
    fallingColor: '#e53935',   // red
    neutralColor: '#fb8c00',   // orange
    showProjection: true,
  },
  envelope: { opacity: 0.10 },
  avgCloud: {
    bullishColor: '#00c853', // matches Pine at_cloud_bull_col
    bearishColor: '#d50000', // matches Pine at_cloud_bear_col
    neutralColor: '#ffd600', // matches Pine at_cloud_neut_col
    opacity:       0.25,
    // `reference` retained only as a no-op for back-compat with old payloads.
  },
  // C-Mid defaults — bumped from line-width 1 (round 32) so the mid line
  // is visible against translucent envelope fills. Pink-magenta picks
  // out the line clearly on either light or dark themes.
  cMid: { color: '#e91e63', lineWidth: 2 },
  bbr: {
    overboughtColor: '#c62828', oversoldColor: '#2e7d32',
    bbLen: 20, bbMult: 2,
    pane: true,
    lineColor: '#5e35b1',      // deep purple — distinct from the bull/bear palette
    lineWidth: 2,
    lineMode: 'step',
    obLevel: 100, osLevel: -100,
    showLtf: true,
    showHtf: false,
    htfColor: '#8e24aa',       // purple 600 — the LTF curve is slope-coloured teal/red
    htfLineWidth: 3,
  },
  vspk: { color: '#f9a825' },
  ltp: { lineColor: '#9e9e9e', lineWidth: 1 },
  sessions: { enabled: false, color: '#90a4ae', opacity: 0.5, style: 'dashed', mode: 'default', nseExpiryDow: 2, nseExpiryTime: '15:30', deltaExpiryTime: '17:30' },
  grid: { opacity: 0.0 },
  markPrice: { enabled: false, color: '#ffb74d' },
  lrBnd: { color: '#9c27b0', opacity: 0.18 },
  fbo: { period: 20 },
  wave: {
    channelLength: 10, averageLength: 21,
    pane: true,
    showWt2: false,            // wt1 is the reading; wt2 is a smoothing detail
    lineColor: '#00897b',      // teal
    wt2Color: '#80cbc4',
    lineWidth: 2,
    lineMode: 'step',
    obLevel: 60, osLevel: -60,
    showLtf: true,
    showHtf: false,
    htfColor: '#f9a825',       // yellow 800 — clear of the teal/red slopes and every mark colour
    htfLineWidth: 3,
  },
  bbrWave: {
    // Real market OHLC — what the rule was measured on. See the note on the type.
    srcCandle: 'Regular',
    slopeColor: true,
    riseColor: '#26a69a',   // the chart's own bull hue, so the pane and the candles agree
    fallColor: '#ef5350',
    showMidline: true,
    // Dimmer than the OB/OS guides (#90a4ae): three reference lines of equal
    // weight in one small pane read as a grid rather than as levels.
    midlineColor: 'rgba(120,130,140,0.38)',
    showSignals: true,
    requireTurn: true,
    buyColor: '#00c853',
    shortColor: '#d50000',
    guideColor: '#90a4ae',
    showZones: true,
    obZoneColor: '#c62828',   // red 800 — overbought is the "do not chase" half
    osZoneColor: '#2e7d32',   // green 800 — oversold is where the measured entry lives
    obZoneOpacity: 0.10,
    osZoneOpacity: 0.10,
    showRelZones: false,
    relAboveColor: '#2e7d32', // green 800 — BBR above Wave
    relBelowColor: '#c62828', // red 800 — BBR at or below Wave
    // Lower than the OB/OS bands on purpose: this one covers the ENTIRE pane,
    // so at their 0.10 it would swamp them and the curves both.
    relZoneOpacity: 0.08,
    zoneOpacity: 0.10,        // deprecated; see the note on the type
    // Exit defaults are the values the figures above were measured with.
    showExits: true,
    exitLevel: 0,
    // 2.5, not 1.5. Measured like-for-like: a 1.5xATR stop cut crypto from
    // +3.90% / 60.3% win to +2.81% / 52.4% and equities from +4.39% / 77.3% to
    // +3.62% / 60.1% - it stops out trades that go on to recover. 2.5xATR keeps
    // almost all of it (crypto +3.60% / 60.0%). 0 disables the stop entirely and
    // measures best of all; it is a risk choice, not a returns one.
    stopAtr: 2.5,
    maxHold: 20,
    exitColor: '#1565c0',   // blue — an exit is neither a win nor a loss colour
    stopColor: '#ef6c00',   // amber — a stop is a different outcome and should look it
    timeColor: '#607d8b',   // grey — maxHold expired; the rule never actually fired
    shortMode: 'off',
    shortTargetAtr: 1.5,
    shortStopAtr: 1.5,
    shortMaxBars: 5,
    htfShowForming: true,
    htfConfluence: false,
    htfConfluenceOpacity: 0.08,
  },
  vpa: { ...VPA_TOGGLE_DEFAULTS },
  lrc: { candleType: 'Regular' },
  mm: { candleType: 'Regular', bullColor: '#26a69a', bearColor: '#ef5350', opacity: 0.22, showLtf: true, showHtf: false, htfOpacity: 0.12 },
  hlDiff: {
    candleType: 'HA', emaPeriod: 21, mode: 'histogram',
    highColor: '#26a69a', lowColor: '#ef5350', lineWidth: 1,
    fillColor: '#90a4ae', fillOpacity: 0.15,
    histBothRiseColor: '#26a69a', histBothFallColor: '#ef5350',
    histHiRiseLoFallColor: '#ef9a9a', histHiFallLoRiseColor: '#a5d6a7',
    histOpacity: 0.8,
  },
  sr: {
    methods: {
      hlDiff: { enabled: true, useCurrentTf: true, useHtf: true, maxLevelsPerTf: 3 },
    },
    resistanceColor: '#ef5350', supportColor: '#26a69a',
    ctfWidth: 1, htfWidth: 2,
    activeOpacity: 0.95, inactiveOpacity: 0.3,
    lineStyle: 2, showLabels: true,
    displayMode: 'levels', bandFillColor: '#90a4ae', bandFillOpacity: 0.12,
    markers: {
      enabled: false, bufFrac: 0.12, window: 3, holds: 2, cooldown: 2, testLookback: 3,
      showConfirmed: true, showFalse: true, showRespected: true,
    },
  },
  emas: {
    ema1: { period: 7,   enabled: true, color: '#ff7043' }, // orange
    ema2: { period: 21,  enabled: true, color: '#ffd54f' }, // amber
    ema3: { period: 51,  enabled: true, color: '#26c6da' }, // cyan
    ema4: { period: 200, enabled: true, color: '#ab47bc' }, // purple
  },
  relStrength: {
    benchmarkSymbol: '',
    benchmarkExchange: '',
    color: '#7e57c2',  // deep purple — distinct from EMA palette
    lineWidth: 2,
    emaPeriod: 7,
    bandFillColor: '#7e57c2',
    bandOpacity: 0.20,
    bandUpColor: '#26a69a',   // greenish — RS above its EMA
    bandDownColor: '#ef5350', // reddish — RS below its EMA
    pane: true,               // R105 — default to the lower indicator pane
  },
  dayChange: {
    color: '#00897b',  // teal — distinct from price action colors
    lineWidth: 2,
    showZeroLine: true,
    lookbackBars: 24,
    emaPeriod: 7,
    bandFillColor: '#00897b',
    bandOpacity: 0.20,
    bandUpColor: '#26a69a',
    bandDownColor: '#ef5350',
    pane: true,               // R105 — default to the lower indicator pane
  },
  macd: {
    fastLength: 12,
    slowLength: 26,
    signalLength: 9,
    macdColor: '#2962ff',   // blue MACD line
    signalColor: '#ff6d00',  // orange signal line
    bandUpColor: '#26a69a',  // greenish — MACD above Signal
    bandDownColor: '#ef5350', // reddish — MACD below Signal
    bandOpacity: 0.20,
    pane: true,               // R105 — default to the lower indicator pane
  },
  synthiya: {
    pcrUpColor: '#26a69a', pcrDownColor: '#ef5350', pcrWidth: 2,
    ivUpColor:  '#42a5f5', ivDownColor:  '#ef6c00', ivWidth:  1,
    rsUpColor:  '#9ccc65', rsDownColor:  '#ab47bc', rsWidth:  1,
  },
  flow: {
    showStrip: true,
  },
  overview: {
    weights: { ema: 1, avgCloud: 1, cpr: 1, cpoc: 1, hpoc: 1, relStrength: 1, chgN: 1, macd: 1, jhm: 1, wrPct: 1 },
    strongThreshold: OVERVIEW_DEFAULT_THRESHOLDS.strong,
    probableThreshold: OVERVIEW_DEFAULT_THRESHOLDS.probable,
    zoneColors: { ...OVERVIEW_ZONE_COLORS },
    upColor: OVERVIEW_SHAPE_COLORS.up,
    downColor: OVERVIEW_SHAPE_COLORS.down,
    neutralColor: OVERVIEW_SHAPE_COLORS.neutral,
    gaugeOpacity: 0.85,
    bgTintOpacity: 0.10,
  },
  bkground: {
    method: 'ctf',         // R119 — legacy combined-trend sign tint by default
    // CLN is the 'stack' default because it is the candle type the rule was
    // described against, and the one whose HTF context band is already drawn on
    // this chart — so the tint agrees with a band the eye can already see.
    srcCandle: 'CLN',
    bullColor: '#26a69a',  // green — combined TF+HTF score > 0
    bearColor: '#ef5350',  // red — combined TF+HTF score ≤ 0
    // Grey, not a third hue: neutral means "no regime to report", and a colour
    // with an opinion would be claiming one.
    neutralColor: '#90a4ae',
    opacity: 0.10,
  },
  funding: {
    positiveColor: '#ef5350', // longs pay shorts (funding > 0)
    negativeColor: '#26a69a', // shorts pay longs (funding < 0)
    lineWidth: 2,
    pane: true,
    signals: true,
    emaLength: 8,
    lookback: 100,
    k: 2,
    showBands: true,
    bandWidth: 2,
  },
  bolev: {
    buyColor: '#26a69a',   // green — BUY breakout level
    shortColor: '#ef5350', // red — SHORT breakout level
    bandOpacity: 0.12,
    lineWidth: 2,
    lookback: 2,
    channelFill: false,
    channelColor: '#42a5f5',
    channelOpacity: 0.08,
  },
  blast: {
    dailyColor: '#42a5f5',   // blue — Daily Inside/Engulfing band (legacy)
    dailyOpacity: 0.16,
    weeklyColor: '#ab47bc',  // purple — Weekly band (legacy)
    weeklyOpacity: 0.12,
    bullColor: '#26a69a',
    bearColor: '#ef5350',
    showDaily: true,
    showWeekly: true,
    showSignals: true,
  },
  trend: {
    color: '#42a5f5',     // blue — TF + HTF individual lines
    opacity: 0.9,
    tfLineWidth: 1,       // chart TF — thin
    htfLineWidth: 3,      // HTF — thick
    smoothing: 'linreg',  // Linear Regression (LSMA) by default — smooth, low lag
    smoothingPeriod: 14,
    showIndividual: true,
    showCombined: true,
    showHma: true,
    combinedColor: '#26c6da',  // cyan — combined (TF+HTF) line
    combinedLineWidth: 2,
    hmaColor: '#ffb300',       // amber — HMA line
    hmaLineWidth: 4,           // thicker
    hmaPeriod: 20,
    bandUpColor: '#26a69a',    // green — combined ≥ HMA (legacy, see bull/bear)
    bandDownColor: '#ef5350',  // red — combined < HMA (legacy)
    bandOpacity: 0.18,
    pane: true,                // R105 — default to the lower indicator pane
    bullColor: '#26a69a',      // R106 — shared bullish color
    bearColor: '#ef5350',      // R106 — shared bearish color
    neutralColor: '#b0bec5',   // R106 — HMA flat
    showDivergence: true,
    zeroAreaOpacity: 0.15,     // above/below-zero area shade
    zoneOpacity: 0.06,         // vertical regime zones (subtle)
  },
  jhm: {
    rsiLength: 9,
    source: 'close',
    vwmaLength: 21,
    emaLength: 3,
    bullShadeColor: '#00C853',
    bullShadeOpacity: 0.12,
    bearShadeColor: '#FF1744',
    bearShadeOpacity: 0.12,
    pane: true,                // R105 — default to the lower indicator pane
  },
  wrPct: {
    // R116b — default source is LinReg Close per user preference.
    source: 'linregClose',
    fastLength: 21,
    fastEma: 13,
    slowLength: 200,
    slowEma: 13,
    fastLineColor: '#FFA000',
    slowLineColor: '#00B8D4',
    bullBandColor: '#00C853',
    bullBandOpacity: 0.22,
    bearBandColor: '#FF1744',
    bearBandOpacity: 0.22,
    pane: true,
  },
  vol: {
    showEmas: true,
    bandBullColor: '#1565c0',
    bandBearColor: '#f57f17',
    bandOpacity: 0.35,
    bullSpikeColor: '#ff6f00',
    bearSpikeColor: '#e040fb',
    fastColor: '#29b6f6',
    slowColor: '#ffb74d',
    pane: true,
  },
  avBand: {
    fastPeriod: 14,
    slowPeriod: 100,
    lineWidth: 2,
    effectiveMode: 'logic1',
    lineMode: 'staircase',
    // Only the Slow HMA is drawn by default. This is the code fallback for a user
    // with NO saved template; anyone who has one keeps their explicit values,
    // because resolvePayload() spreads the saved payload over these.
    showFast: false,
    showSlow: true,
    showBand: false,
    showEffective: false,
    effectiveWidth: 1,
    fastColor: '#26a69a',
    slowColor: '#ef5350',
    effectiveColor: '#90a4ae',
    bandBullColor: '#26a69a',
    bandBearColor: '#ef5350',
    bandOpacity: 0.25,
    showTrendBand: false,
    trendBullColor: '#26a69a',
    trendBearColor: '#ef5350',
    trendOpacity: 0.15,
  },
  bollinger: {
    variant: 'traditional',
    period: 20,
    stdMult: 2,
    showMiddle: true,
    fillEnabled: true,
    fillColor: '#2962ff',
    fillOpacity: 0.08,
    lineMode: 'line',
    lineWidth: 1,
    bandColor: '#2962ff',
    middleColor: '#ff6d00',
  },
  vob: {
    stLen: 50,
    stMult: 3.5,
    showFill: true,
    cloudBullColor: '#00ffcc',
    cloudBearColor: '#ff007f',
    pivotLen: 7,
    bullColor: '#00ffcc',
    bearColor: '#ff007f',
    txtColor: '#e0e0e0',
    deleteOnBreak: true,
    showBullRetest: true,
    bullVolPct: 50,
    bullSigColor: '#00ffcc',
    showBearRetest: true,
    bearVolPct: 50,
    bearSigColor: '#ff007f',
  },
  fno: {
    // Rolling tier: D0 (single-select — the D0/W0/M0 chips set exactly one).
    tierD: true, tierW: false, tierM: false,
    showSR: true, srCount: 1, showMaxPain: true, showGexFlip: true,
    showWalls: false, wallCount: 2,
    showOiRange: true, showExpMove: false, showRegime: false, showRolls: true, showGaps: true,
    showPcr: true, showPcrEma: true, pcrEmaLength: 7,
    showIv: true, showIvEma: false, ivEmaLength: 7,
    showOi: true, oiMode: 'net',
    lineMode: 'historical',
    oiRangeColor: '#5c6bc0', expMoveColor: '#90a4ae',
    pcrColor: '#7e57c2', pcrEmaColor: '#ff9800', ivColor: '#26c6da', ivEmaColor: '#ec407a',
    days: 30,
    srColor: '#90a4ae',       // legacy (no longer used for S/R)
    srSupportColor: '#089981',    // bullish green — OI support
    srResistanceColor: '#f23645', // bearish red — OI resistance
    maxPainColor: '#ffb300',  // amber
    gexFlipColor: '#ab47bc',  // purple
    wallPosColor: '#26a69a',  // green — long gamma
    wallNegColor: '#ef5350',  // red — short gamma
    payoffTpColor: '#26a69a', // green — basket take-profit underlying level
    payoffSlColor: '#ef5350', // red — basket stop-loss underlying level
  },
  // NOTE: resolvePayload only keeps keys that exist here — a settings group written by
  // the dialog but MISSING from these defaults is silently stripped on reload (that was
  // the "DbCrZone colours never apply" bug). Every dialog group needs a block below.
  dbcr: {
    debitColor: '#ef5350',   // trending / premium-expansion regime
    creditColor: '#26a69a',  // range / premium-decay regime
    opacity: 0.12,
  },
  bhairav: {
    candleType: 'FOLLOW',
    investInr: 100000,
  },
  omSimple: {
    clnLength: 50,
    suppressThinVolume: false,
    showWatch: true,
    showContinuation: true,
    watchThreshold: 45,
    continuationThreshold: 55,
  },
  vajra: {
    bbOs: -100,
    wvOs: -60,
    requireTurn: true,
    minHtfRun: 2,
    exitLevel: 0,
    stopAtr: 2.5,
    maxHold: 20,
    riskPct: 1,
  },
  clhBand: {
    showTf: true,
    showHtf: false,
    showBandLines: true,
    showFlip: false,
    showFill: true,
    showLabels: true,
    upperColor: '#26a69a',
    lowerColor: '#ef5350',
    flipFallColor: '#26a69a',   // green — a FALLING midline is the bullish one
    flipRiseColor: '#ef5350',   // red — a RISING midline is the bearish one
    flipAboveColor: '#26a69a',  // deprecated; kept so old templates survive
    flipBelowColor: '#ef5350',  // deprecated; kept so old templates survive
    fillBullColor: '#26a69a',
    fillBearColor: '#ef5350',
    fillOpacity: 0.10,
    lineWidth: 2,
    lineWidthHtf: 2,
    lineStyle: 0,
    lineStyleHtf: 2,            // dashed — how the HTF half has always drawn
    htfOpacityScale: 0.55,
  },
  clnBreak: {
    lookback: 3,
    bufferAtr: 0,
    breach: 'touch',
    requireLrAgree: false,
    stopAtr: 0,
    showLevels: true,
    buyColor: '#43a047',
    shortColor: '#e53935',
    lineWidth: 2,
    lineStyle: 0,
  },
  gandiv: {
    source: 'HA',
    showBand: true,
    showEntry: true,
    showSl: true,
    showLabels: true,
    bandUpColor: '#26a69a',
    bandDownColor: '#ef5350',
    bandOpacity: 0.12,
    longColor: '#00c853',
    shortColor: '#ff5252',
    slColor: '#ff9800',
    lineWidth: 2,
  },
  lrCross: { source: 'Regular' },
  htfBand: { showForming: true, opacity: 0.12 },
  candleFraction: { denominator: 1 },
  profitZone: { m1: 2.5, m2: 4, color: '#f5a623', fillOpacity: 0.1, lineWidth: 1, fitScale: true },
  usePerSymbolSaved: true
};

/** Deep-merge partial payload over defaults. Two levels deep covers the schema. */
/**
 * Probe/diagnostic seam. Chart settings live server-side on the active template,
 * so a strictly read-only probe had no way to reach a setting without CLICKING
 * through the settings dialog and SAVING — which would mutate the user's real
 * template. The probe policy's answer to that is to make the state addressable
 * instead, so this reads an override object out of localStorage and layers it
 * over the resolved payload.
 *
 * Never written by application code. Group-level merge, matching resolvePayload.
 * Logs when active so a forgotten override can be found rather than puzzled over.
 *
 *   localStorage.setItem('chart_settings_override_v1',
 *     JSON.stringify({ candle: { clnHtfBand: true } }))
 */
const SETTINGS_OVERRIDE_KEY = 'chart_settings_override_v1';

function readSettingsOverride(): Partial<ChartSettingsPayload> | null {
  try {
    const raw = localStorage.getItem(SETTINGS_OVERRIDE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    // eslint-disable-next-line no-console
    console.warn('[chartSettings] local override active:', Object.keys(parsed).join(', '),
      `— clear with localStorage.removeItem('${SETTINGS_OVERRIDE_KEY}')`);
    return parsed as Partial<ChartSettingsPayload>;
  } catch { return null; }
}

/** Layer the local override (if any) over an already-resolved payload. */
export function applySettingsOverride(base: ChartSettingsPayload): ChartSettingsPayload {
  const ov = readSettingsOverride();
  if (!ov) return base;
  const out: any = { ...base };
  for (const k of Object.keys(ov)) {
    const v = (ov as any)[k];
    const cur = (base as any)[k];
    out[k] = (v && typeof v === 'object' && !Array.isArray(v) && cur && typeof cur === 'object' && !Array.isArray(cur))
      ? { ...cur, ...v }
      : v;
  }
  return out as ChartSettingsPayload;
}

export function resolvePayload(partial?: Partial<ChartSettingsPayload>): ChartSettingsPayload {
  const out: any = {};
  const d = DEFAULT_CHART_SETTINGS as any;
  const p = (partial as any) || {};
  for (const k of Object.keys(d)) {
    if (typeof d[k] === 'object' && d[k] && !Array.isArray(d[k])) {
      out[k] = { ...d[k], ...(p[k] || {}) };
    } else {
      out[k] = p[k] !== undefined ? p[k] : d[k];
    }
  }
  return out as ChartSettingsPayload;
}

async function list(): Promise<ChartSettingsTemplate[]> {
  const resp = await api.get<any>('/chart-settings');
  return ((resp.data as any)?.data || []) as ChartSettingsTemplate[];
}

async function create(name: string, payload: Partial<ChartSettingsPayload>, activate = false): Promise<ChartSettingsTemplate> {
  const resp = await api.post<any>('/chart-settings', { name, payload, activate });
  return (resp.data as any)?.data as ChartSettingsTemplate;
}

async function update(id: number, patch: { name?: string; payload?: Partial<ChartSettingsPayload> }): Promise<ChartSettingsTemplate> {
  const resp = await api.put<any>(`/chart-settings/${id}`, patch);
  return (resp.data as any)?.data as ChartSettingsTemplate;
}

async function remove(id: number): Promise<void> {
  await api.delete<any>(`/chart-settings/${id}`);
}

async function activate(id: number): Promise<ChartSettingsTemplate> {
  const resp = await api.post<any>(`/chart-settings/${id}/activate`);
  return (resp.data as any)?.data as ChartSettingsTemplate;
}

/** Make MY current chart template + app settings the live default new users are seeded from. */
async function setNewUserDefault(): Promise<{ sourceUserId: number; isMe: boolean }> {
  const resp = await api.post<any>('/chart-settings/new-user-default');
  return (resp.data as any)?.data;
}
async function getNewUserDefault(): Promise<{ sourceUserId: number | null; isMe: boolean }> {
  const resp = await api.get<any>('/chart-settings/new-user-default');
  return (resp.data as any)?.data;
}

export const chartSettingsService = { list, create, update, remove, activate, resolvePayload, setNewUserDefault, getNewUserDefault };
